/**
 * OxyMed product catalogue
 * ------------------------
 * Vanilla JavaScript, no dependencies.
 *
 * - Safe to load multiple times: guarded by a window flag and
 *   customElements.get() checks, so duplicate <script> tags are harmless.
 * - Uses custom elements, so sections re-initialise automatically when the
 *   Shopify Theme Editor re-renders them (no shopify:section:load wiring
 *   needed) and multiple catalogue sections on one page never conflict.
 * - Progressive enhancement: without JavaScript, every rendered product is
 *   visible (nothing is hidden in the markup) and the "Load more" button
 *   stays hidden. This script hides the overflow items and reveals the
 *   button only when it can actually do something.
 */
(function () {
  'use strict';

  if (window.__oxymedCatalogueLoaded) return;
  window.__oxymedCatalogueLoaded = true;

  var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function scrollBehavior() {
    return reducedMotionQuery.matches ? 'auto' : 'smooth';
  }

  /* ------------------------------------------------------------------ *
   * <oxymed-category> — independent "Load more" for one category grid   *
   * ------------------------------------------------------------------ */
  if (!customElements.get('oxymed-category')) {
    customElements.define(
      'oxymed-category',
      class OxymedCategory extends HTMLElement {
        connectedCallback() {
          if (this.__oxymedBound) return;
          this.__oxymedBound = true;

          this.items = Array.prototype.slice.call(
            this.querySelectorAll('[data-oxymed-item]')
          );
          this.button = this.querySelector('[data-oxymed-loadmore]');
          this.status = this.querySelector('[data-oxymed-status]');

          if (!this.button || this.items.length === 0) return;

          this.initial = this.readCount('initial', 8);
          this.step = this.readCount('step', 8);
          this.visibleCount = Math.min(this.initial, this.items.length);

          this.button.addEventListener('click', this.onLoadMore.bind(this));
          this.render(null);
        }

        readCount(key, fallback) {
          var value = parseInt(this.dataset[key], 10);
          return isNaN(value) || value < 1 ? fallback : value;
        }

        onLoadMore() {
          var previousCount = this.visibleCount;
          this.visibleCount = Math.min(
            this.visibleCount + this.step,
            this.items.length
          );
          this.render(previousCount);
        }

        render(previousCount) {
          var visibleCount = this.visibleCount;

          this.items.forEach(function (item, index) {
            item.hidden = index >= visibleCount;
          });

          this.button.hidden = visibleCount >= this.items.length;

          if (this.status) {
            this.status.textContent =
              'Showing ' + visibleCount + ' of ' + this.items.length + ' products';
          }

          /* After "Load more", move focus to the first newly revealed
             product so keyboard and screen-reader users land on the new
             content instead of a button that may have just disappeared. */
          if (previousCount !== null && previousCount < visibleCount) {
            var firstNewItem = this.items[previousCount];
            var focusTarget = firstNewItem
              ? firstNewItem.querySelector('a[href]')
              : null;
            if (focusTarget) focusTarget.focus();
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * <oxymed-catalogue-nav> — pill navigation with smooth scrolling      *
   * ------------------------------------------------------------------ */
  if (!customElements.get('oxymed-catalogue-nav')) {
    customElements.define(
      'oxymed-catalogue-nav',
      class OxymedCatalogueNav extends HTMLElement {
        connectedCallback() {
          if (this.__oxymedBound) {
            /* Re-attached after a Theme Editor move: restart the scroll-spy
               observer that disconnectedCallback tore down. */
            this.observeCategories();
            return;
          }
          this.__oxymedBound = true;

          this.links = Array.prototype.slice.call(
            this.querySelectorAll('a[href^="#"]')
          );

          this.addEventListener('click', this.onClick.bind(this));
          this.observeCategories();
        }

        onClick(event) {
          var link = event.target.closest ? event.target.closest('a[href^="#"]') : null;
          if (!link || !this.contains(link)) return;

          var id;
          try {
            id = decodeURIComponent(link.getAttribute('href').slice(1));
          } catch (error) {
            id = link.getAttribute('href').slice(1);
          }
          var target = document.getElementById(id);
          if (!target) return; /* fall back to default anchor behaviour */

          event.preventDefault();

          /* scroll-margin-top on the target handles the sticky-header offset */
          target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
          target.focus({ preventScroll: true });
          this.setActive(link);
        }

        setActive(activeLink) {
          this.links.forEach(function (link) {
            if (link === activeLink) {
              link.setAttribute('aria-current', 'true');
            } else {
              link.removeAttribute('aria-current');
            }
          });

          /* Keep the active pill visible inside the horizontal scroller. */
          if (activeLink && typeof activeLink.scrollIntoView === 'function') {
            try {
              activeLink.scrollIntoView({
                behavior: scrollBehavior(),
                block: 'nearest',
                inline: 'nearest'
              });
            } catch (error) {
              /* Older browsers: ignore. */
            }
          }
        }

        /* Highlight the pill of the category currently in view. */
        observeCategories() {
          if (!('IntersectionObserver' in window)) return;

          var targetToLink = new Map();
          this.links.forEach(function (link) {
            var id;
            try {
              id = decodeURIComponent(link.getAttribute('href').slice(1));
            } catch (error) {
              id = link.getAttribute('href').slice(1);
            }
            var target = document.getElementById(id);
            if (target) targetToLink.set(target, link);
          });

          if (targetToLink.size === 0) return;

          var self = this;
          this.__oxymedObserver = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                  self.setActiveQuiet(targetToLink.get(entry.target));
                }
              });
            },
            { rootMargin: '-25% 0px -65% 0px' }
          );

          targetToLink.forEach(function (link, target) {
            self.__oxymedObserver.observe(target);
          });
        }

        /* Same as setActive but never scrolls the pill row (scroll-spy). */
        setActiveQuiet(activeLink) {
          this.links.forEach(function (link) {
            if (link === activeLink) {
              link.setAttribute('aria-current', 'true');
            } else {
              link.removeAttribute('aria-current');
            }
          });
        }

        disconnectedCallback() {
          if (this.__oxymedObserver) {
            this.__oxymedObserver.disconnect();
            this.__oxymedObserver = null;
          }
        }
      }
    );
  }
})();
